import {
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Grid,
    GridItem,
    IconButton,
    Text,
    Tooltip,
    useBoolean,
    useOutsideClick,
    useToast
} from '@chakra-ui/react'
import { Icon } from 'components/atoms'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { useAuthContext } from 'contexts/auth'
import { useContractContext } from 'contexts/contract'
import { environments } from 'environments/environments'
import { ModalSignature } from 'pages/components/ModalSignature'
import { FormModalSignature } from 'pages/components/ModalSignature/types'
import React, { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import ContractService from 'services/contracts'
import ViewComplementaryDataForm from '../ViewForms/ViewComplementaryDataForm'
import ViewConsortiumForm from '../ViewForms/ViewConsortiumForm'
import ViewContractForm from '../ViewForms/ViewContractForm'
import ViewCreditorForm from '../ViewForms/ViewCreditorForm'
import ViewDebtorForm from '../ViewForms/ViewDebtorForm'
import ViewFinancingForm from '../ViewForms/ViewFinancingForm'
import ViewFormFileData from '../ViewForms/ViewFormFileData'
import SendingFiles from '../ViewForms/ViewFormFileData/Components/SendingFiles'
import { ViewFormFileDataProps } from '../ViewForms/ViewFormFileData/interfaces'
import ViewThirdGuarantorForm from '../ViewForms/ViewThirdGuarantorForm'
import ViewVehicleForm from '../ViewForms/ViewVehicleForm'
import { AttachmentProps, ContractDetailProps } from './interfaces'
import { AccordionButtonCustom, AccordionCustom, Form } from './styles'

interface ContractDetailFCProps {
    contractSelected: ContractDetailProps | undefined
}

interface AddButtonProps {
    clicked: () => void
}

const ContractDetail: React.FC<ContractDetailFCProps> = ({
    contractSelected
}) => {
    const methods = useForm<ContractDetailProps>({
        defaultValues: contractSelected
    })
    
    useEffect(() => {
        if (contractSelected) {
            methods.reset(contractSelected)
        }
    }, [contractSelected, methods])

    const toast = useToast()
    const { id } = useParams()
    const refModalSignature = useRef<ModalBuildHandle>(null)
    const { endUploadFile, setEndUploadFile, setFileData, getContract} =
        useContractContext()

    const { isBackoffice } = useAuthContext()

    const handleOpenUploadFileModal = () => {
        setTooltipState.toggle()
        modalUploadRef.current?.onOpen()
    }

    const accordions = [
        {
            label: 'Dados do veículo',
            content: <ViewVehicleForm />
        },
        {
            label: 'Dados do contrato',
            content: <ViewContractForm />
        },
        {
            label: 'Dados complementares',
            content: <ViewComplementaryDataForm />
        },
        {
            label: 'Dados do financiamento',
            content: <ViewFinancingForm />
        },
        {
            label: 'Empresas administradoras de consórcio',
            content: <ViewConsortiumForm />
        },
        {
            label: 'Dados do credor',
            content: <ViewCreditorForm />
        },
        {
            label: 'Dados do devedor',
            content: <ViewDebtorForm />
        },
        {
            label: 'Dados do terceiro garantidor',
            content: <ViewThirdGuarantorForm />
        },        
        {
            label: 'Anexos',
            content: (
                <ViewFormFileData
                    attachments={contractSelected?.anexos as AttachmentProps[]}
                    openModal={handleOpenUploadFileModal}
                />
            )
        }
    ]

    const modalUploadRef = useRef<ModalBuildHandle>(null)

    const handleCloseUploadFileModal = () => {
        modalUploadRef.current?.onClose()
        setFileData({} as ViewFormFileDataProps)
    }

    const clearFileUpload = () => {
        setFileData({} as ViewFormFileDataProps)
    }

    const markUploadAsFinished = () => {
        setEndUploadFile(false)
    }

    useEffect(() => {
        markUploadAsFinished()
    }, [])

    const toogleModalSignature = (action: string) => {
        if (action === 'close') {
            refModalSignature.current?.onClose()
            return
        }

        refModalSignature.current?.onOpen()
    }

    const AddButton: React.FC<AddButtonProps> = ({ clicked }) => {
        return (
            <IconButton
                float="right"
                top="-35px"
                right="44px"
                colorScheme="primary"
                aria-label="Search database"
                icon={<Icon name="add" />}
                onClick={clicked}
            />
        )
    }

    const refTooltip = useRef(null)

    const [tooltipState, setTooltipState] = useBoolean(false)
    useOutsideClick({
        ref: refTooltip,
        handler: setTooltipState.off
    })

    const handleSelectWithSignature = () => {
        setTooltipState.toggle();
        // 1. Limpa o estado interno da modal antes de abrir
        signatureModalControllerRef.current?.initialState(); 
        toogleModalSignature('open');
    };

    const signatureModalControllerRef = useRef<any>(null)

    const onSubmitModalSignature = async (value: FormModalSignature) => {
        try {
            const client = window.SerproSignerClient;
            const arquivoFisico = value.file.anexo.file;

            if (!client.isConnected()) {
                await new Promise((res) => client.connect(res));
                await new Promise(res => setTimeout(res, 500));
            }

            const responsePreparar = await ContractService.preparsignatureContact(
                id || '', 
                arquivoFisico
            );

            const { documentoId, hashBase64 } = responsePreparar.data;

            const assinaturaFinal: any = await new Promise((resolve, reject) => {
                client.sign("HASH", hashBase64, "BASE64", "BASE64", false)
                    .success((res: any) => resolve(res.signature || res))
                    .error((err: any) => reject(err));
            });

            await ContractService.finalizeSignatureContact(id || '', documentoId, assinaturaFinal);

            toast({ title: 'Sucesso!', description: 'Documento assinado com sucesso.', status: 'success' });
            
            toogleModalSignature('close');

            if (id) {
                await getContract(id); 
            }

            // 2. Notifica o contexto para recarregar os arquivos (isso atualiza a lista na tela)
            setEndUploadFile(true); // Abre o accordion de anexos
            
        } catch (e: any) {
            console.error(e);
            toast({ title: 'Erro', description: 'Falha ao assinar.', status: 'error' });
        }
    };

    const PopoverSignature: React.FC = () => {
        return (
            <Box position="relative">
                <Tooltip
                    ref={refTooltip}
                    isOpen={tooltipState}
                    bg="white"
                    placement="top" // Positioning above the button
                    label={
                        <Box
                            bg="white"
                            width="150px"
                            borderRadius="8px"
                            pointerEvents="auto"
                            textAlign="center"
                            paddingY="12px"
                            border="0"
                        >
                            <Button
                                width="100%"
                                height="32px"
                                colorScheme="off-white"
                                color="black"
                                _hover={{
                                    background: 'grayscale.50'
                                }}
                                onClick={handleSelectWithSignature}
                            >
                                Assinar documento
                            </Button>

                            <Button
                                width="100%"
                                height="32px"
                                colorScheme="off-white"
                                color="black"
                                _hover={{
                                    background: 'grayscale.50'
                                }}
                                onClick={handleOpenUploadFileModal}
                            >
                                Anexar documento
                            </Button>
                        </Box>
                    }
                >
                    <IconButton
                        float="right"
                        top="-35px"
                        right="44px"
                        colorScheme="primary"
                        aria-label="Search database"
                        icon={<Icon name="add" />}
                        onClick={setTooltipState.toggle}
                    />
                </Tooltip>
            </Box>
        )
    }

    const { SIGNATURE_ATTACHMENTS } = environments

    const renderAttachments = (label: string) => {
        if (isBackoffice) return
        if (label !== 'Anexos') return
        return (
            <>
                {SIGNATURE_ATTACHMENTS ? (
                    <PopoverSignature />
                ) : (
                    <AddButton clicked={handleOpenUploadFileModal} />
                )}
            </>
        )
    }

    return (
        <Grid gap={{ base: 2 }} px={6} py={4}>
            <GridItem>
                <FormProvider {...methods}>
                    <Form>
                        <ModalBuild
                            ref={modalUploadRef}
                            closeOnOverlayClick={false}
                            onAfterCloseRefresh={clearFileUpload}
                            title=" "
                            size="2xl"
                        >
                            <SendingFiles
                                onClose={handleCloseUploadFileModal}
                            />
                        </ModalBuild>

                        <ModalSignature
                            refModal={refModalSignature}
                            ref={signatureModalControllerRef}                        
                            onCancel={() => toogleModalSignature('close')}
                            onSubmit={onSubmitModalSignature}
                        />

                        <AccordionCustom
                            allowToggle
                            defaultIndex={endUploadFile ? [8] : []}
                        >
                            {accordions &&
                                accordions.map(({ label, content }, key) => (
                                    <AccordionItem key={key}>
                                        <div>
                                            <AccordionButtonCustom position="relative">
                                                <Box flex="1" textAlign="left">
                                                    <Text fontWeight="700">
                                                        {label}
                                                    </Text>
                                                </Box>
                                                <AccordionIcon fontSize="24px" />
                                            </AccordionButtonCustom>
                                            {renderAttachments(label)}
                                        </div>

                                        <AccordionPanel p={'16px 0px'}>
                                            {content}
                                        </AccordionPanel>
                                    </AccordionItem>
                                ))}
                        </AccordionCustom>
                    </Form>
                </FormProvider>
            </GridItem>
        </Grid>
    )
}

export default ContractDetail
